import { useState } from 'react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDropdownToggle = (menu: string) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://static.readdy.ai/image/8766ff501ca5aa7943b16e981fd86369/5736cedd4c67ca9b1d62f6fef5d0cc2c.png" 
              alt="財恩工房ロゴ" 
              className="w-10 h-10 object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: "Pacifico, serif" }}>
              財恩工房
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {/* サービスについて */}
            <div className="relative group">
              <button className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer flex items-center space-x-1">
                <span>サービスについて</span>
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link to="/story" className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  ストーリー
                </Link>
                <Link to="/features" className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  できること
                </Link>
                <Link to="/how-it-works" className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  しくみ
                </Link>
                <Link to="/safety" className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  安全・FAQ
                </Link>
                <Link to="/news" className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  ニュース
                </Link>
                <a 
                  href="https://note.com/b0ratasu_bene" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors cursor-pointer"
                >
                  note
                </a>
              </div>
            </div>

            {/* 利用者の方へ */}
            <div className="relative group">
              <button className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer flex items-center space-x-1">
                <span>利用者の方へ</span>
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link to="/for-places" className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  居場所の皆さまへ
                </Link>
                <Link to="/for-volunteers" className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  ボランティアの皆さまへ
                </Link>
              </div>
            </div>

            {/* 操作マニュアル */}
            <Link to="/manual" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap">
              操作マニュアル
            </Link>

            {/* お問い合わせ */}
            <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap">
              お問い合わせ
            </Link>

            {/* おすすめ教材 */}
            <a 
              href="https://kyo-colle.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              おすすめ教材
            </a>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="https://boratasu.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              サービスを使ってみる
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 cursor-pointer"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-2">
              {/* サービスについて */}
              <div>
                <button
                  onClick={() => handleDropdownToggle('app')}
                  className="w-full flex items-center justify-between text-gray-700 hover:text-blue-600 transition-colors py-2 cursor-pointer"
                  aria-expanded={openDropdown === 'app'}
                >
                  <span className="font-medium">サービスについて</span>
                  <i className={`ri-arrow-${openDropdown === 'app' ? 'up' : 'down'}-s-line`}></i>
                </button>
                {openDropdown === 'app' && (
                  <div className="pl-4 mt-2 space-y-2">
                    <Link 
                      to="/story" 
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      ストーリー
                    </Link>
                    <Link 
                      to="/features" 
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      できること
                    </Link>
                    <Link 
                      to="/how-it-works" 
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      しくみ
                    </Link>
                    <Link 
                      to="/safety" 
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      安全・FAQ
                    </Link>
                    <Link 
                      to="/news" 
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      ニュース
                    </Link>
                    <a 
                      href="https://note.com/b0ratasu_bene" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2 cursor-pointer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      note
                    </a>
                  </div>
                )}
              </div>

              {/* 利用者の方へ */}
              <div>
                <button
                  onClick={() => handleDropdownToggle('users')}
                  className="w-full flex items-center justify-between text-gray-700 hover:text-blue-600 transition-colors py-2 cursor-pointer"
                  aria-expanded={openDropdown === 'users'}
                >
                  <span className="font-medium">利用者の方へ</span>
                  <i className={`ri-arrow-${openDropdown === 'users' ? 'up' : 'down'}-s-line`}></i>
                </button>
                {openDropdown === 'users' && (
                  <div className="pl-4 mt-2 space-y-2">
                    <Link 
                      to="/for-places" 
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      居場所の皆さまへ
                    </Link>
                    <Link 
                      to="/for-volunteers" 
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      ボランティアの皆さまへ
                    </Link>
                  </div>
                )}
              </div>

              {/* 操作マニュアル */}
              <Link 
                to="/manual" 
                className="text-gray-700 hover:text-blue-600 transition-colors py-2 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                操作マニュアル
              </Link>

              {/* お問い合わせ */}
              <Link 
                to="/contact" 
                className="text-gray-700 hover:text-blue-600 transition-colors py-2 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                お問い合わせ
              </Link>

              {/* おすすめ教材 */}
              <a 
                href="https://kyo-colle.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600 transition-colors py-2 font-medium cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                おすすめ教材
              </a>

              <div className="flex flex-col space-y-3 pt-4">
                <a 
                  href="https://boratasu.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  サービスを使ってみる
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
